import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { FiPackage, FiSearch, FiDownload, FiPrinter, FiGrid, FiTrendingUp, FiTrendingDown, FiDollarSign, FiChevronUp, FiChevronDown, FiX, FiRefreshCw } from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore';
import { recordReads } from '../../utils/readTelemetry';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import { formatNumber } from '../../utils/formatters';
import { fromBaseQty, twoUnitsModuleOn, getTwoUnitPair } from '../../utils/unitConversion';
import useCurrency from '../../hooks/useCurrency';
import { inActiveFy } from '../../utils/stockFlowHelper';
import { getCached } from '../../utils/fyDataCache';
import { addPrintBrand } from '../../utils/printBranding';

// A stock line's number, also when it was saved as text ("12,791,310").
const toNum = (v) => {
  const n = typeof v === 'string' ? Number(v.replace(/[,\s]/g, '')) : Number(v);
  return Number.isFinite(n) ? n : 0;
};

// ── Session cache (owner-approved 2026-08-05) ──────────────────────────────
// This report used to re-buy 5 collections — including the WHOLE stockFlow
// history (~100k docs) — every time Generate was pressed, and From/To date and
// Location sat in the fetch dependency array so touching any of them forced a
// complete refetch. Measured at 204,825 reads in ONE day for a single company.
//
// Now the raw rows are fetched once per company + financial year + branch and
// kept in the module-level fyDataCache (survives navigating away and back);
// dates, location, category, search, grouping and sorting are all applied to
// those cached rows. The header shows "Data as of <time>" and a Refresh button
// re-fetches live, so a stale figure can never be printed unnoticed — which is
// why fyDataCache's "reports must always fetch live" rule is relaxed here.
const CACHE_PREFIX = 'invBalances';

// Dual-unit qty: if item has units[] and a default sale unit (or larger pack),
// render as "10 Ctn (240 Pcs)". Falls back to plain "{baseQty}" otherwise.
const dualQtyDisplay = (item, baseQty) => {
  const baseStr = formatNumber(baseQty, { decimals: 2 });
  if (!item || !Array.isArray(item.units) || item.units.length < 2) return baseStr;
  // Show Two Units: an item with a chosen pair shows stock in those two units.
  const pair = twoUnitsModuleOn() ? getTwoUnitPair(item) : null;
  if (pair) {
    const u = { units: item.units, unitOfMeasure: item.unit || '' };
    return `${formatNumber(fromBaseQty(u, baseQty, pair[0]), { decimals: 2 })} ${pair[0]} (${formatNumber(fromBaseQty(u, baseQty, pair[1]), { decimals: 2 })} ${pair[1]})`;
  }
  const baseUnit = item.unit || '';
  const targetCode = item.defaultSaleUnit
    || (item.units.find(u => !u.isBase && Number(u.conversion) > 1)?.unitCode);
  if (!targetCode || targetCode === baseUnit) return baseStr;
  const targetRow = item.units.find(u => u.unitCode === targetCode);
  if (!targetRow || !(Number(targetRow.conversion) > 1)) return baseStr;
  const targetQty = fromBaseQty({ units: item.units, unitOfMeasure: baseUnit }, baseQty, targetCode);
  return `${formatNumber(targetQty, { decimals: 2 })} ${targetCode} (${baseStr} ${baseUnit})`;
};

// Styled Components
const FiltersCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  margin-bottom: 20px;
`;

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-end;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 160px;
`;

// "Data as of ..." — the report is served from a session cache, so the fetch
// time must always be visible next to the figures.
const AsOfRow = styled.div`
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid ${colors.neutral[200]};
  font-size: 12px;
  color: ${colors.text.secondary};
  text-align: right;

  strong {
    color: ${colors.text.primary};
    font-weight: 600;
  }
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  color: ${colors.text.primary};
  background: white;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  color: ${colors.text.primary};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const SearchInput = styled.div`
  position: relative;
  flex: 1;
  min-width: 200px;
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${colors.text.secondary};
`;

const SearchField = styled.input`
  width: 100%;
  padding: 10px 12px 10px 40px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  color: ${colors.text.primary};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  margin-left: auto;
`;

const Button = styled.button`
  padding: 10px 16px;
  border: none;
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
  background: ${colors.neutral[100]};
  color: ${colors.text.primary};

  &:hover {
    background: ${colors.neutral[200]};
  }

  ${props => props.primary && `
    background: ${colors.primary.main};
    color: white;

    &:hover:not(:disabled) {
      background: ${colors.primary.dark};
    }
  `}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
`;

const SummaryCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SummaryIconWrap = styled.div`
  width: 44px;
  height: 44px;
  border-radius: ${colors.borderRadius.md};
  background: ${props => props.color}15;
  color: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
`;

const SummaryInfo = styled.div`
  flex: 1;
`;

const SummaryLabel = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-bottom: 4px;
`;

const SummaryValue = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: ${colors.text.primary};
`;

const ContentCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  overflow: hidden;
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: ${colors.neutral[50]};
`;

const Th = styled.th`
  padding: 12px 16px;
  text-align: ${props => props.align || 'left'};
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid ${colors.neutral[200]};
  white-space: nowrap;
  cursor: ${props => props.sortable ? 'pointer' : 'default'};
  user-select: none;
  transition: color 0.2s;

  &:hover {
    ${props => props.sortable && `color: ${colors.text.primary};`}
  }

  ${props => props.$active && `color: ${colors.primary.main};`}
`;

const ThContent = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const SortIcon = styled.span`
  display: inline-flex;
  flex-direction: column;
  font-size: 10px;
  line-height: 0;
  color: ${colors.neutral[400]};

  svg {
    font-size: 12px;
  }

  .active {
    color: ${colors.primary.main};
  }
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid ${colors.neutral[100]};
  transition: background 0.2s;
  cursor: ${props => props.clickable ? 'pointer' : 'default'};

  &:hover {
    background: ${colors.neutral[50]};
  }

  ${props => props.isCategory && `
    background: ${colors.neutral[50]};
    font-weight: 600;
    cursor: default;
  `}
`;

const TotalsRow = styled.tr`
  background: ${colors.neutral[100]};
  font-weight: 600;
  border-top: 2px solid ${colors.neutral[300]};
`;

const Td = styled.td`
  padding: 10px 16px;
  font-size: 13px;
  color: ${colors.text.primary};
  text-align: ${props => props.align || 'left'};
`;

const AmountCell = styled(Td)`
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: 12px;
`;

const ItemCodeBadge = styled.span`
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: 12px;
  padding: 2px 8px;
  background: ${colors.primary.main}10;
  color: ${colors.primary.main};
  border-radius: ${colors.borderRadius.sm};
  font-weight: 500;
`;

const CategoryBadge = styled.span`
  display: inline-flex;
  padding: 2px 8px;
  background: ${colors.info.main}10;
  color: ${colors.info.main};
  border-radius: ${colors.borderRadius.sm};
  font-size: 11px;
  font-weight: 500;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${colors.text.secondary};
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  color: ${colors.neutral[300]};
  margin-bottom: 16px;
`;

const EmptyText = styled.p`
  font-size: 16px;
  margin-bottom: 8px;
`;

const InventoryBalances = () => {
  const { activeCompany, activeBranch, activeFinancialYear } = useAuth();
  const { formatCurrency } = useCurrency();
  const navigate = useNavigate();
  // Raw (unaggregated) data straight from Firestore / the session cache:
  // { fetchedAt, categoriesList, subCategoriesList, locationsList, itemMasterMap, stockFlow }
  const [raw, setRaw] = useState(null);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSubCategory, setSelectedSubCategory] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [groupBy, setGroupBy] = useState('none');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortColumn, setSortColumn] = useState('itemCode');
  const [sortDirection, setSortDirection] = useState('asc');
  const [imagePreview, setImagePreview] = useState(null); // { url, itemName }

  useEffect(() => {
    // Default date range: current financial year start to today
    const today = new Date();
    const fyStart = new Date(today.getFullYear(), today.getMonth() >= 6 ? 6 : -6, 1); // July start
    setDateFrom(fyStart.toISOString().split('T')[0]);
    setDateTo(today.toISOString().split('T')[0]);
  }, []);

  // Light master-data load for the filter dropdowns (categories / sub-categories / locations).
  // The heavy stockFlow fetch is gated behind the Generate button.
  const loadFilterMasters = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const [categoriesSnap, subCategoriesSnap, locationsSnap] = await Promise.all([
        getDocs(query(collection(db, `companies/${activeCompany.id}/inventoryCategories`), orderBy('categoryCode'))),
        getDocs(query(collection(db, `companies/${activeCompany.id}/inventorySubCategories`), orderBy('subCategoryCode'))),
        getDocs(query(collection(db, `companies/${activeCompany.id}/inventoryLocations`), orderBy('locationCode')))
      ]);
      setCategories(categoriesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(c => c.isDeleted !== true));
      setSubCategories(subCategoriesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(s => s.isDeleted !== true));
      setLocations(locationsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error('Error loading filter masters:', error);
    }
  }, [activeCompany]);

  useEffect(() => {
    loadFilterMasters();
  }, [loadFilterMasters]);

  // Fetch the RAW data only. Nothing here depends on the date range or the
  // location any more — those are user filters and are applied further down over
  // these rows, so changing them costs 0 Firestore reads.
  // `forceRefresh` = the Refresh button: bypasses the cache and pulls live.
  const loadData = useCallback(async (forceRefresh = false) => {
    if (!activeCompany?.id) return;

    const fyId = activeFinancialYear?.id || null;
    const branchId = (!activeBranch || activeBranch.id === 'all') ? 'all' : activeBranch.id;
    // Company + financial year + branch are the only things that change the raw
    // set, so they alone make up the cache key.
    const cacheKey = `${CACHE_PREFIX}|${activeCompany.id}|${fyId || 'nofy'}|${branchId}`;

    try {
      setLoading(true);

      const bundle = await getCached(cacheKey, async () => {
        // Load master data and stock flow in parallel
        const [categoriesSnap, subCategoriesSnap, locationsSnap, itemsSnap, stockFlowSnap] = await Promise.all([
          getDocs(query(collection(db, `companies/${activeCompany.id}/inventoryCategories`), orderBy('categoryCode'))),
          getDocs(query(collection(db, `companies/${activeCompany.id}/inventorySubCategories`), orderBy('subCategoryCode'))),
          getDocs(query(collection(db, `companies/${activeCompany.id}/inventoryLocations`), orderBy('locationCode'))),
          getDocs(query(collection(db, `companies/${activeCompany.id}/inventoryItems`), orderBy('itemCode'))),
          // Server-side mirror of the client rule below (`status && status !==
          // 'Draft'/'Cancelled'`): not-in also requires the field to exist, so
          // the result set is identical — Draft/Cancelled rows just stop being
          // downloaded. All-years stays (legacy untagged rows must keep counting
          // via inActiveFy; a server-side year filter would drop them).
          getDocs(query(collection(db, `companies/${activeCompany.id}/stockFlow`),
            where('status', 'not-in', ['Draft', 'Cancelled'])))
        ]);
        recordReads(activeCompany.id,
          categoriesSnap.size + subCategoriesSnap.size + locationsSnap.size + itemsSnap.size + stockFlowSnap.size);

        const categoriesList = categoriesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(c => c.isDeleted !== true);
        const subCategoriesList = subCategoriesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(s => s.isDeleted !== true);
        const locationsList = locationsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        // Build item master map
        const itemMasterMap = {};
        itemsSnap.docs.forEach(doc => {
          const d = doc.data();
          itemMasterMap[doc.id] = {
            itemCode: d.itemCode || '',
            barcode: d.barcode != null ? String(d.barcode).trim() : '',
            itemName: d.itemName || '',
            categoryId: d.categoryId || '',
            subCategoryId: d.subCategoryId || '',
            unitOfMeasure: d.unitOfMeasure || d.unit || 'Pcs',
            imageUrl: d.imageUrl || '',
            units: Array.isArray(d.units) ? d.units : [],
            defaultSaleUnit: d.defaultSaleUnit || '',
            showUnits: Array.isArray(d.showUnits) ? d.showUnits : []
          };
        });

        // Posted stock flow entries only.
        // ACTIVE FINANCIAL YEAR ONLY — a new year's opening stock already contains
        // last year's closing, so counting both would double every item (96 → 192).
        // Year + branch are part of the cache key, so scoping here is safe.
        const stockFlow = stockFlowSnap.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(sf => sf.status && sf.status !== 'Draft' && sf.status !== 'Cancelled')
          .filter(sf => inActiveFy(sf, fyId))
          .filter(t => branchId === 'all' || t.branchId === branchId);

        return { fetchedAt: Date.now(), categoriesList, subCategoriesList, locationsList, itemMasterMap, stockFlow };
      }, undefined, forceRefresh);

      setCategories(bundle.categoriesList);
      setSubCategories(bundle.subCategoriesList);
      setLocations(bundle.locationsList);
      setRaw(bundle);

    } catch (error) {
      console.error('Error loading inventory balances:', error);
    } finally {
      setLoading(false);
    }
  }, [activeCompany, activeBranch, activeFinancialYear]);

  // Aggregate the cached raw rows into report rows. Date range and location are
  // applied HERE (they were already client-side filters — they were just sitting
  // in the fetch dependency array), so changing them re-computes instantly
  // instead of re-downloading ~100,000 stock rows.
  const data = useMemo(() => {
    if (!raw) return [];

    // Build category/subcategory lookup
    const categoryMap = {};
    raw.categoriesList.forEach(c => { categoryMap[c.id] = c.categoryName || ''; });
    const subCategoryMap = {};
    raw.subCategoriesList.forEach(s => { subCategoryMap[s.id] = s.subCategoryName || ''; });
    const itemMasterMap = raw.itemMasterMap;

    // Aggregate by item
    const itemAgg = {};

    raw.stockFlow.forEach(sf => {
      if (!sf.itemId) return;

      // Location filter
      if (selectedLocation !== 'all' && sf.locationId !== selectedLocation) return;

      const key = sf.itemId;
      if (!itemAgg[key]) {
        const master = itemMasterMap[sf.itemId] || {};
        itemAgg[key] = {
          itemId: sf.itemId,
          itemCode: master.itemCode || sf.itemCode || '',
          barcode: master.barcode || '',
          itemName: master.itemName || sf.itemName || '',
          categoryId: master.categoryId || '',
          subCategoryId: master.subCategoryId || '',
          categoryName: categoryMap[master.categoryId] || 'Uncategorized',
          subCategoryName: subCategoryMap[master.subCategoryId] || '',
          unit: master.unitOfMeasure || 'Pcs',
          units: master.units || [],
          defaultSaleUnit: master.defaultSaleUnit || '',
          showUnits: master.showUnits || [],
          imageUrl: master.imageUrl || '',
          openingQty: 0,
          openingValue: 0,
          qtyIn: 0,
          valueIn: 0,
          qtyOut: 0,
          valueOut: 0
        };
      }

      const item = itemAgg[key];
      // Numbers saved as text ("12,791,310") must not join up as text (→ NaN).
      const qty = toNum(sf.quantity);
      const cost = toNum(sf.totalCost);
      const docDate = sf.docDate || '';

      // Latest receipt cost up to the report date (2026-11-05) — shown as the
      // Unit Cost when nothing is left in stock, instead of 0.00.
      if (sf.direction === 'in' && qty > 0 && docDate <= dateTo) {
        const at = docDate + '|' + String(sf.createdAt || '');
        if (!item.lastInAt || at >= item.lastInAt) { item.lastInAt = at; item.lastInCost = cost / qty; }
      }

      if (docDate < dateFrom) {
        // Opening balance: before date range
        if (sf.direction === 'in') {
          item.openingQty += qty;
          item.openingValue += cost;
        } else {
          item.openingQty -= qty;
          item.openingValue -= cost;
        }
      } else if (docDate >= dateFrom && docDate <= dateTo) {
        // Within date range
        if (sf.direction === 'in') {
          item.qtyIn += qty;
          item.valueIn += cost;
        } else {
          item.qtyOut += qty;
          item.valueOut += cost;
        }
      }
      // After dateTo: ignore
    });

    // Calculate closing balances
    const resultData = Object.values(itemAgg).map(item => {
      const closingQty = item.openingQty + item.qtyIn - item.qtyOut;
      const closingValue = item.openingValue + item.valueIn - item.valueOut;
      const avgCost = closingQty > 0 ? closingValue / closingQty : (item.lastInCost || 0);
      return {
        ...item,
        closingQty,
        closingValue: Math.max(0, closingValue),
        avgCost
      };
    });

    // Sort by item code
    resultData.sort((a, b) => a.itemCode.localeCompare(b.itemCode));

    return resultData;
  }, [raw, dateFrom, dateTo, selectedLocation]);

  // Summary cards — over ALL generated rows (not the category/search filtered
  // view), exactly as before.
  const summary = useMemo(() => ({
    totalItems: data.length,
    totalOpeningValue: data.reduce((s, i) => s + Math.max(0, i.openingValue), 0),
    totalQtyIn: data.reduce((s, i) => s + i.qtyIn, 0),
    totalQtyOut: data.reduce((s, i) => s + i.qtyOut, 0),
    totalClosingValue: data.reduce((s, i) => s + i.closingValue, 0)
  }), [data]);

  // When the raw rows were pulled from Firestore — shown on screen and on the
  // printout so a stale stock report can't go out unnoticed.
  const asOfLabel = raw?.fetchedAt ? new Date(raw.fetchedAt).toLocaleString() : '';

  // Heavy stockFlow fetch is user-triggered only (Generate button) — no auto-load on mount.
  // Generate serves the session cache when it's warm (0 reads); Refresh forces a live re-fetch.
  const handleGenerate = (forceRefresh = false) => {
    if (!dateFrom || !dateTo) return;
    setHasGenerated(true);
    loadData(forceRefresh);
  };

  // Filtered subcategories based on selected category
  const filteredSubCategories = selectedCategory === 'all'
    ? subCategories
    : subCategories.filter(sc => sc.categoryId === selectedCategory);

  useEffect(() => {
    setSelectedSubCategory('all');
  }, [selectedCategory]);

  // Filter data
  const filteredData = data.filter(item => {
    const matchesSearch = !searchQuery ||
      item.itemName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.itemCode?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || item.categoryId === selectedCategory;
    const matchesSubCategory = selectedSubCategory === 'all' || item.subCategoryId === selectedSubCategory;
    return matchesSearch && matchesCategory && matchesSubCategory;
  });

  // Sort handler
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Sort helper
  const renderSortTh = (label, column, align) => (
    <Th
      align={align}
      sortable
      $active={sortColumn === column}
      onClick={() => handleSort(column)}
    >
      <ThContent>
        {label}
        <SortIcon>
          <FiChevronUp className={sortColumn === column && sortDirection === 'asc' ? 'active' : ''} />
          <FiChevronDown className={sortColumn === column && sortDirection === 'desc' ? 'active' : ''} />
        </SortIcon>
      </ThContent>
    </Th>
  );

  // Apply sorting to filtered data
  const sortedData = [...filteredData].sort((a, b) => {
    let valA = a[sortColumn];
    let valB = b[sortColumn];

    // String columns
    if (['itemCode', 'barcode', 'itemName', 'categoryName', 'unit'].includes(sortColumn)) {
      valA = (valA || '').toLowerCase();
      valB = (valB || '').toLowerCase();
      const cmp = valA.localeCompare(valB);
      return sortDirection === 'asc' ? cmp : -cmp;
    }

    // Numeric columns
    valA = Number(valA) || 0;
    valB = Number(valB) || 0;
    return sortDirection === 'asc' ? valA - valB : valB - valA;
  });

  // Group items if needed
  const getGroupedItems = () => {
    if (groupBy === 'none') {
      return [{ name: null, items: sortedData }];
    }

    const groups = {};
    sortedData.forEach(item => {
      const key = groupBy === 'category'
        ? item.categoryName
        : (locations.find(l => l.id === selectedLocation)?.locationName || 'All Locations');
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    });

    return Object.entries(groups).map(([name, items]) => ({ name, items }));
  };

  // Calculate filtered totals
  const filteredTotals = filteredData.reduce((acc, item) => ({
    openingQty: acc.openingQty + item.openingQty,
    openingValue: acc.openingValue + Math.max(0, item.openingValue),
    qtyIn: acc.qtyIn + item.qtyIn,
    valueIn: acc.valueIn + item.valueIn,
    qtyOut: acc.qtyOut + item.qtyOut,
    valueOut: acc.valueOut + item.valueOut,
    closingQty: acc.closingQty + item.closingQty,
    closingValue: acc.closingValue + item.closingValue
  }), { openingQty: 0, openingValue: 0, qtyIn: 0, valueIn: 0, qtyOut: 0, valueOut: 0, closingQty: 0, closingValue: 0 });

  const handleExport = () => {
    let csv = 'Inventory Balances Report\n';
    csv += `Period: ${dateFrom} to ${dateTo}\n\n`;
    csv += 'Item Code,Barcode,Item Name,Category,Unit,Opening Qty,Opening Value,Qty In,Value In,Qty Out,Value Out,Closing Qty,Unit Cost,Closing Value\n';

    filteredData.forEach(item => {
      csv += `${item.itemCode},`;
      // ="..." keeps long barcodes as text in Excel (no 8.96E+12)
      csv += item.barcode ? `"=""${item.barcode.replace(/"/g, '')}""",` : ',';
      csv += `"${(item.itemName || '').replace(/"/g, '""')}",`;
      csv += `"${(item.categoryName || '').replace(/"/g, '""')}",`;
      csv += `${item.unit},`;
      csv += `${item.openingQty.toFixed(2)},`;
      csv += `${Math.max(0, item.openingValue).toFixed(2)},`;
      csv += `${item.qtyIn.toFixed(2)},`;
      csv += `${item.valueIn.toFixed(2)},`;
      csv += `${item.qtyOut.toFixed(2)},`;
      csv += `${item.valueOut.toFixed(2)},`;
      csv += `${item.closingQty.toFixed(2)},`;
      csv += `${item.avgCost.toFixed(2)},`;
      csv += `${item.closingValue.toFixed(2)}\n`;
    });

    csv += `\nTotal Items:,${summary.totalItems}\n`;
    csv += `Total Opening Value:,${summary.totalOpeningValue.toFixed(2)}\n`;
    csv += `Total Closing Value:,${summary.totalClosingValue.toFixed(2)}\n`;

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Inventory_Balances_${dateFrom}_${dateTo}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // qtyOnly = true prints a stock-count sheet: quantity columns only, no values,
  // no cost. Useful for physical counts / sharing stock levels without exposing
  // purchase costs and stock valuation.
  // qtyOnly: false = full print, true = quantities only, 'cost' = quantities +
  // Unit Cost (no values) — 2026-11-05.
  const handlePrint = (qtyOnly = false) => {
    if (filteredData.length === 0) { alert('No data to print'); return; }

    const withCost = qtyOnly === 'cost';
    const title = withCost ? 'Inventory Balances (Quantities & Unit Cost)' : (qtyOnly ? 'Inventory Balances (Quantities)' : 'Inventory Balances');
    const colCount = withCost ? 10 : (qtyOnly ? 9 : 14);

    const summaryHtml = qtyOnly
      ? `
        <div class="summary-item"><div class="summary-label">Total Items</div><div class="summary-value">${summary.totalItems}</div></div>
        <div class="summary-item"><div class="summary-label">Total Qty In</div><div class="summary-value in">${formatNumber(summary.totalQtyIn, { decimals: 2 })}</div></div>
        <div class="summary-item"><div class="summary-label">Total Qty Out</div><div class="summary-value out">${formatNumber(summary.totalQtyOut, { decimals: 2 })}</div></div>`
      : `
        <div class="summary-item"><div class="summary-label">Total Items</div><div class="summary-value">${summary.totalItems}</div></div>
        <div class="summary-item"><div class="summary-label">Total Opening Value</div><div class="summary-value">${formatCurrency(summary.totalOpeningValue, { decimals: 2 })}</div></div>
        <div class="summary-item"><div class="summary-label">Total Qty In</div><div class="summary-value in">${formatNumber(summary.totalQtyIn, { decimals: 2 })}</div></div>
        <div class="summary-item"><div class="summary-label">Total Qty Out</div><div class="summary-value out">${formatNumber(summary.totalQtyOut, { decimals: 2 })}</div></div>
        <div class="summary-item"><div class="summary-label">Total Closing Value</div><div class="summary-value">${formatCurrency(summary.totalClosingValue, { decimals: 2 })}</div></div>`;

    const headHtml = qtyOnly
      ? `<th>Item Code</th><th>Barcode</th><th>Item Name</th><th>Category</th><th>Unit</th>
         <th class="right">Opening Qty</th><th class="right">Qty In</th>
         <th class="right">Qty Out</th><th class="right">Closing Qty</th>${withCost ? '<th class="right">Unit Cost</th>' : ''}`
      : `<th>Item Code</th><th>Barcode</th><th>Item Name</th><th>Category</th><th>Unit</th>
         <th class="right">Opening Qty</th><th class="right">Opening Value</th>
         <th class="right">Qty In</th><th class="right">Value In</th>
         <th class="right">Qty Out</th><th class="right">Value Out</th>
         <th class="right">Closing Qty</th><th class="right">Unit Cost</th><th class="right">Closing Value</th>`;

    const rowHtml = (item) => qtyOnly
      ? `<tr>
          <td>${item.itemCode || '-'}</td>
          <td>${item.barcode || '-'}</td>
          <td>${item.itemName}</td>
          <td>${item.categoryName}</td>
          <td style="text-align:center">${item.unit}</td>
          <td class="amount">${formatNumber(item.openingQty, { decimals: 2 })}</td>
          <td class="amount in">${formatNumber(item.qtyIn, { decimals: 2 })}</td>
          <td class="amount out">${formatNumber(item.qtyOut, { decimals: 2 })}</td>
          <td class="amount">${dualQtyDisplay(item, item.closingQty)}</td>${withCost ? `
          <td class="amount">${formatCurrency(item.avgCost, { decimals: 2 })}</td>` : ''}
        </tr>`
      : `<tr>
          <td>${item.itemCode || '-'}</td>
          <td>${item.barcode || '-'}</td>
          <td>${item.itemName}</td>
          <td>${item.categoryName}</td>
          <td style="text-align:center">${item.unit}</td>
          <td class="amount">${formatNumber(item.openingQty, { decimals: 2 })}</td>
          <td class="amount">${formatCurrency(Math.max(0, item.openingValue), { decimals: 2 })}</td>
          <td class="amount in">${formatNumber(item.qtyIn, { decimals: 2 })}</td>
          <td class="amount">${formatCurrency(item.valueIn, { decimals: 2 })}</td>
          <td class="amount out">${formatNumber(item.qtyOut, { decimals: 2 })}</td>
          <td class="amount">${formatCurrency(item.valueOut, { decimals: 2 })}</td>
          <td class="amount">${dualQtyDisplay(item, item.closingQty)}</td>
          <td class="amount">${formatCurrency(item.avgCost, { decimals: 2 })}</td>
          <td class="amount">${formatCurrency(item.closingValue, { decimals: 2 })}</td>
        </tr>`;

    const totalsHtml = qtyOnly
      ? `<tr class="totals-row">
          <td colspan="5" style="text-align:right">Totals</td>
          <td class="amount">${formatNumber(filteredTotals.openingQty, { decimals: 2 })}</td>
          <td class="amount">${formatNumber(filteredTotals.qtyIn, { decimals: 2 })}</td>
          <td class="amount">${formatNumber(filteredTotals.qtyOut, { decimals: 2 })}</td>
          <td class="amount">${formatNumber(filteredTotals.closingQty, { decimals: 2 })}</td>${withCost ? `
          <td class="amount">-</td>` : ''}
        </tr>`
      : `<tr class="totals-row">
          <td colspan="5" style="text-align:right">Totals</td>
          <td class="amount">${formatNumber(filteredTotals.openingQty, { decimals: 2 })}</td>
          <td class="amount">${formatCurrency(filteredTotals.openingValue, { decimals: 2 })}</td>
          <td class="amount">${formatNumber(filteredTotals.qtyIn, { decimals: 2 })}</td>
          <td class="amount">${formatCurrency(filteredTotals.valueIn, { decimals: 2 })}</td>
          <td class="amount">${formatNumber(filteredTotals.qtyOut, { decimals: 2 })}</td>
          <td class="amount">${formatCurrency(filteredTotals.valueOut, { decimals: 2 })}</td>
          <td class="amount">${formatNumber(filteredTotals.closingQty, { decimals: 2 })}</td>
          <td class="amount">-</td>
          <td class="amount">${formatCurrency(filteredTotals.closingValue, { decimals: 2 })}</td>
        </tr>`;

    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>${title}</title>
      <style>
        body{font-family:'Segoe UI',sans-serif;margin:30px;color:#1A1A1A;font-size:11px}
        h1{color:#800020;font-size:20px;margin-bottom:5px}
        .meta{color:#666;font-size:11px;margin-bottom:18px}
        .summary{display:flex;gap:24px;margin-bottom:18px;padding:12px 15px;background:#f9f9f9;border-radius:8px;flex-wrap:wrap}
        .summary-item{text-align:center;min-width:100px}
        .summary-label{font-size:10px;color:#666;margin-bottom:3px}
        .summary-value{font-size:15px;font-weight:600}
        .in{color:#1565C0} .out{color:#C62828}
        table{width:100%;border-collapse:collapse;margin-bottom:15px}
        th{background:#800020;color:white;padding:5px 6px;text-align:left;font-size:9px;text-transform:uppercase}
        th.right{text-align:right}
        td{padding:5px 6px;border-bottom:1px solid #ddd;font-size:10px}
        .amount{text-align:right;font-family:'Consolas',monospace}
        .category-row{background:#f0f0f0;font-weight:600;font-size:11px}
        .totals-row{background:#f5f5f5;font-weight:700;border-top:2px solid #800020}
        @media print{body{margin:15px}@page{size:landscape}}
      </style></head><body>
      <h1>${title}</h1>
      <div class="meta">
        ${activeCompany?.companyName || 'Company'}${activeBranch?.branchName ? ' | Branch: ' + activeBranch.branchName : ''} |
        Period: ${dateFrom} to ${dateTo}${asOfLabel ? ' | Data as of: ' + asOfLabel : ''}
      </div>
      <div class="summary">${summaryHtml}
      </div>
      <table>
        <thead><tr>${headHtml}
        </tr></thead>
        <tbody>
          ${getGroupedItems().map(group => `
            ${group.name ? `<tr class="category-row"><td colspan="${colCount}">${group.name} (${group.items.length} items)</td></tr>` : ''}
            ${group.items.map(rowHtml).join('')}
          `).join('')}
          ${totalsHtml}
        </tbody>
      </table>
      <div style="font-size:10px;color:#999;margin-top:10px">Printed on ${new Date().toLocaleString()}</div>
    </body></html>`);
    addPrintBrand(w);
    w.document.close();
    w.print();
  };

  return (
    <PageLayout pageTitle="Inventory Balances" breadcrumb="Reports / Inventory Reports / Inventory Balances">
      {hasGenerated && !loading && (
      <SummaryCards>
        <SummaryCard>
          <SummaryIconWrap color={colors.primary.main}>
            <FiPackage />
          </SummaryIconWrap>
          <SummaryInfo>
            <SummaryLabel>Total Items</SummaryLabel>
            <SummaryValue>{summary.totalItems}</SummaryValue>
          </SummaryInfo>
        </SummaryCard>
        <SummaryCard>
          <SummaryIconWrap color={colors.info.main}>
            <FiDollarSign />
          </SummaryIconWrap>
          <SummaryInfo>
            <SummaryLabel>Total Opening Value</SummaryLabel>
            <SummaryValue>{formatCurrency(summary.totalOpeningValue, { decimals: 2 })}</SummaryValue>
          </SummaryInfo>
        </SummaryCard>
        <SummaryCard>
          <SummaryIconWrap color={colors.success.main}>
            <FiTrendingUp />
          </SummaryIconWrap>
          <SummaryInfo>
            <SummaryLabel>Total Qty In</SummaryLabel>
            <SummaryValue>{formatNumber(summary.totalQtyIn, { decimals: 2 })}</SummaryValue>
          </SummaryInfo>
        </SummaryCard>
        <SummaryCard>
          <SummaryIconWrap color={colors.error.main}>
            <FiTrendingDown />
          </SummaryIconWrap>
          <SummaryInfo>
            <SummaryLabel>Total Qty Out</SummaryLabel>
            <SummaryValue>{formatNumber(summary.totalQtyOut, { decimals: 2 })}</SummaryValue>
          </SummaryInfo>
        </SummaryCard>
        <SummaryCard>
          <SummaryIconWrap color={colors.warning.main}>
            <FiGrid />
          </SummaryIconWrap>
          <SummaryInfo>
            <SummaryLabel>Total Closing Value</SummaryLabel>
            <SummaryValue>{formatCurrency(summary.totalClosingValue, { decimals: 2 })}</SummaryValue>
          </SummaryInfo>
        </SummaryCard>
      </SummaryCards>
      )}

      <FiltersCard>
        <FiltersRow>
          <FilterGroup>
            <Label>From Date</Label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </FilterGroup>

          <FilterGroup>
            <Label>To Date</Label>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </FilterGroup>

          <FilterGroup>
            <Label>Category</Label>
            <Select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <option value="all">All Categories</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.categoryName}</option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Sub Category</Label>
            <Select
              value={selectedSubCategory}
              onChange={(e) => setSelectedSubCategory(e.target.value)}
            >
              <option value="all">All Sub Categories</option>
              {filteredSubCategories.map(sc => (
                <option key={sc.id} value={sc.id}>{sc.subCategoryName}</option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Location</Label>
            <Select
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
            >
              <option value="all">All Locations</option>
              {locations.map(loc => (
                <option key={loc.id} value={loc.id}>{loc.locationName}</option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Group By</Label>
            <Select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
            >
              <option value="none">No Grouping</option>
              <option value="category">By Category</option>
            </Select>
          </FilterGroup>

          <SearchInput>
            <SearchIcon><FiSearch /></SearchIcon>
            <SearchField
              type="text"
              placeholder="Search by item code or name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </SearchInput>

          <ButtonGroup>
            <Button onClick={handleExport} disabled={filteredData.length === 0}>
              <FiDownload /> Export
            </Button>
            <Button onClick={() => handlePrint(false)} disabled={filteredData.length === 0}>
              <FiPrinter /> Print
            </Button>
            <Button onClick={() => handlePrint(true)} disabled={filteredData.length === 0} title="Print quantities only — no values or cost">
              <FiPrinter /> Print (Qty Only)
            </Button>
            <Button onClick={() => handlePrint('cost')} disabled={filteredData.length === 0} title="Print quantities with unit cost — no values">
              <FiPrinter /> Print (Qty + Unit Cost)
            </Button>
            <Button primary onClick={() => handleGenerate(false)} disabled={loading || !dateFrom || !dateTo}>
              <FiPackage /> {loading ? 'Loading...' : 'Generate'}
            </Button>
            <Button
              onClick={() => handleGenerate(true)}
              disabled={loading || !dateFrom || !dateTo}
              title="Re-fetch live data from the server (ignores the session cache)"
            >
              <FiRefreshCw /> Refresh
            </Button>
          </ButtonGroup>
        </FiltersRow>

        {raw && (
          <AsOfRow>
            Data as of <strong>{asOfLabel}</strong> — date, location and category filters apply instantly; click <strong>Refresh</strong> for live figures.
          </AsOfRow>
        )}
      </FiltersCard>

      <ContentCard>
        <TableContainer>
          {loading ? (
            <EmptyState>
              <EmptyText>Loading inventory balances...</EmptyText>
            </EmptyState>
          ) : !hasGenerated ? (
            <EmptyState>
              <EmptyIcon><FiPackage /></EmptyIcon>
              <EmptyText>Select your filters and click Generate to view inventory balances</EmptyText>
            </EmptyState>
          ) : filteredData.length === 0 ? (
            <EmptyState>
              <EmptyIcon><FiPackage /></EmptyIcon>
              <EmptyText>
                {searchQuery || selectedCategory !== 'all'
                  ? 'No items found matching your criteria'
                  : 'No inventory data found for the selected period'}
              </EmptyText>
            </EmptyState>
          ) : (
            <Table>
              <Thead>
                <Tr>
                  <Th style={{ width: 50 }}>Image</Th>
                  {renderSortTh('Item Code', 'itemCode')}
                  {renderSortTh('Barcode', 'barcode')}
                  {renderSortTh('Item Name', 'itemName')}
                  {renderSortTh('Category', 'categoryName')}
                  {renderSortTh('Unit', 'unit', 'center')}
                  {renderSortTh('Opening Qty', 'openingQty', 'right')}
                  {renderSortTh('Opening Value', 'openingValue', 'right')}
                  {renderSortTh('Qty In', 'qtyIn', 'right')}
                  {renderSortTh('Value In', 'valueIn', 'right')}
                  {renderSortTh('Qty Out', 'qtyOut', 'right')}
                  {renderSortTh('Value Out', 'valueOut', 'right')}
                  {renderSortTh('Closing Qty', 'closingQty', 'right')}
                  {renderSortTh('Unit Cost', 'avgCost', 'right')}
                  {renderSortTh('Closing Value', 'closingValue', 'right')}
                </Tr>
              </Thead>
              <Tbody>
                {getGroupedItems().map((group, groupIndex) => (
                  <React.Fragment key={groupIndex}>
                    {group.name && (
                      <Tr isCategory>
                        <Td colSpan={15}>
                          <CategoryBadge>{group.name}</CategoryBadge>
                          <span style={{ marginLeft: 12, color: colors.text.secondary, fontWeight: 400, fontSize: 12 }}>
                            ({group.items.length} items)
                          </span>
                        </Td>
                      </Tr>
                    )}
                    {group.items.map((item, idx) => (
                      <Tr
                        key={`${item.itemId}_${idx}`}
                        clickable
                        onClick={() => navigate('/reports/inventory-ledger', { state: { itemId: item.itemId, itemName: item.itemName } })}
                      >
                        <Td style={{ padding: '4px 8px' }}>
                          {item.imageUrl ? (
                            <ItemThumb
                              src={item.imageUrl}
                              alt={item.itemName}
                              onClick={(e) => { e.stopPropagation(); setImagePreview({ url: item.imageUrl, itemName: item.itemName }); }}
                            />
                          ) : (
                            <ItemThumbEmpty><FiPackage size={14} /></ItemThumbEmpty>
                          )}
                        </Td>
                        <Td><ItemCodeBadge>{item.itemCode || '-'}</ItemCodeBadge></Td>
                        <Td>{item.barcode || '-'}</Td>
                        <Td>{item.itemName}</Td>
                        <Td><CategoryBadge>{item.categoryName}</CategoryBadge></Td>
                        <Td align="center">{item.unit}</Td>
                        <AmountCell align="right">{formatNumber(item.openingQty, { decimals: 2 })}</AmountCell>
                        <AmountCell align="right">{formatCurrency(Math.max(0, item.openingValue), { decimals: 2 })}</AmountCell>
                        <AmountCell align="right">{formatNumber(item.qtyIn, { decimals: 2 })}</AmountCell>
                        <AmountCell align="right">{formatCurrency(item.valueIn, { decimals: 2 })}</AmountCell>
                        <AmountCell align="right">{formatNumber(item.qtyOut, { decimals: 2 })}</AmountCell>
                        <AmountCell align="right">{formatCurrency(item.valueOut, { decimals: 2 })}</AmountCell>
                        <AmountCell align="right">{dualQtyDisplay(item, item.closingQty)}</AmountCell>
                        <AmountCell align="right">{formatCurrency(item.avgCost, { decimals: 2 })}</AmountCell>
                        <AmountCell align="right">{formatCurrency(item.closingValue, { decimals: 2 })}</AmountCell>
                      </Tr>
                    ))}
                  </React.Fragment>
                ))}

                {/* Totals Row */}
                <TotalsRow>
                  <Td colSpan={6} style={{ textAlign: 'right', fontWeight: 600 }}>Totals</Td>
                  <AmountCell align="right" style={{ fontWeight: 600 }}>{formatNumber(filteredTotals.openingQty, { decimals: 2 })}</AmountCell>
                  <AmountCell align="right" style={{ fontWeight: 600 }}>{formatCurrency(filteredTotals.openingValue, { decimals: 2 })}</AmountCell>
                  <AmountCell align="right" style={{ fontWeight: 600 }}>{formatNumber(filteredTotals.qtyIn, { decimals: 2 })}</AmountCell>
                  <AmountCell align="right" style={{ fontWeight: 600 }}>{formatCurrency(filteredTotals.valueIn, { decimals: 2 })}</AmountCell>
                  <AmountCell align="right" style={{ fontWeight: 600 }}>{formatNumber(filteredTotals.qtyOut, { decimals: 2 })}</AmountCell>
                  <AmountCell align="right" style={{ fontWeight: 600 }}>{formatCurrency(filteredTotals.valueOut, { decimals: 2 })}</AmountCell>
                  <AmountCell align="right" style={{ fontWeight: 600 }}>{formatNumber(filteredTotals.closingQty, { decimals: 2 })}</AmountCell>
                  <AmountCell align="right" style={{ fontWeight: 600 }}>-</AmountCell>
                  <AmountCell align="right" style={{ fontWeight: 600 }}>{formatCurrency(filteredTotals.closingValue, { decimals: 2 })}</AmountCell>
                </TotalsRow>
              </Tbody>
            </Table>
          )}
        </TableContainer>
      </ContentCard>

      {/* Image Preview Modal */}
      {imagePreview && (
        <ImageOverlay onClick={() => setImagePreview(null)}>
          <ImageModal onClick={(e) => e.stopPropagation()}>
            <ImageModalHeader>
              <ImageModalTitle>{imagePreview.itemName}</ImageModalTitle>
              <ImageCloseBtn onClick={() => setImagePreview(null)}><FiX size={18} /></ImageCloseBtn>
            </ImageModalHeader>
            <ImageModalBody>
              <PreviewImage src={imagePreview.url} alt={imagePreview.itemName} />
            </ImageModalBody>
          </ImageModal>
        </ImageOverlay>
      )}
    </PageLayout>
  );
};

// Image styled components
const ItemThumb = styled.img`
  width: 36px;
  height: 36px;
  border-radius: ${colors.borderRadius.sm};
  object-fit: cover;
  border: 1px solid ${colors.neutral[200]};
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: scale(1.15);
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  }
`;

const ItemThumbEmpty = styled.div`
  width: 36px;
  height: 36px;
  border-radius: ${colors.borderRadius.sm};
  border: 1px dashed ${colors.neutral[300]};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.neutral[400]};
`;

const ImageOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ImageModal = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  max-width: 600px;
  max-height: 80vh;
  width: 90%;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const ImageModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 20px;
  border-bottom: 1px solid ${colors.neutral[200]};
`;

const ImageModalTitle = styled.h3`
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: ${colors.text.primary};
`;

const ImageCloseBtn = styled.button`
  background: none;
  border: none;
  color: ${colors.text.secondary};
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;

  &:hover {
    background: ${colors.neutral[100]};
    color: ${colors.text.primary};
  }
`;

const ImageModalBody = styled.div`
  padding: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  max-height: calc(80vh - 60px);
  overflow: auto;
`;

const PreviewImage = styled.img`
  max-width: 100%;
  max-height: 60vh;
  object-fit: contain;
  border-radius: ${colors.borderRadius.md};
`;

export default InventoryBalances;
